import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock3,
  Gift,
  Info,
  LockKeyhole,
  MapPin,
  Minus,
  Package,
  Plus,
  ShoppingBag,
  Tag,
  Truck,
} from "lucide-react";

import PublicNavbar from "../../components/PublicNavbar";
import { useCart } from "../../../cart/useCart";
import { checkPublicDeliveryAddress, fetchPublicDeliverySettings } from "../../../delivery/deliveryPublicApi";
import { fetchPublicSettings } from "../../../settings/settingsPublicApi";
import { validateCheckoutCoupon } from "../../../coupons/couponsPublicApi";
import { DELIVERY_SCHEDULE_KEY, cartItemsForCheckout, saveCheckoutContext } from "../../../checkout/checkoutContext";
import { validateCheckoutCart } from "../../../checkout/checkoutPublicApi";
import "./cart-page.css";

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function cartItemName(item) {
  return item.item_name_snapshot || item.category_name || item.label || "Menu item";
}

function cartItemBadge(item) {
  return item.variant_name_snapshot || item.variant_name || "";
}

function cartItemSecondBadge(item) {
  const flavorTotal = (item.flavors || []).reduce(
    (sum, flavor) => sum + Number(flavor.quantity || 0),
    0,
  );

  return flavorTotal > 1 ? "Mix & Match Flavors" : "";
}

function cartItemDetails(item) {
  const flavors = item.flavors || [];

  if (!flavors.length) {
    return "Selections will be confirmed at checkout.";
  }

  const flavorTotal = flavors.reduce(
    (sum, flavor) => sum + Number(flavor.quantity || 0),
    0,
  );

  if (flavorTotal > 1) {
    return flavors.map((flavor) => `${flavor.label} x${flavor.quantity}`).join(", ");
  }

  return `Flavor: ${flavors[0].label}`;
}

function cartItemImage(item) {
  return item.image || "";
}

function firstPaidDeliveryFee(deliverySettings) {
  const paidZone = (deliverySettings?.zones || []).find(
    (zone) => zone.zone_type === "flat_fee" && Number(zone.flat_fee) > 0,
  );
  return paidZone ? money(paidZone.flat_fee) : 0;
}

function cartItemEditLabel(item) {
  return cartItemSecondBadge(item) ? "Edit flavors" : "Edit";
}

const emptyAddress = {
  street: "",
  apartment: "",
  city: "",
  state: "MD",
  zip: "",
};

function readSchedule() {
  if (typeof window === "undefined") return { date: "", time: "" };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DELIVERY_SCHEDULE_KEY) || "{}");
    return { date: parsed.date || "", time: parsed.time || "" };
  } catch {
    return { date: "", time: "" };
  }
}

function saveSchedule(schedule) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DELIVERY_SCHEDULE_KEY, JSON.stringify(schedule));
}

function toDateInputValue(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function timeToMinutes(value, fallback) {
  const [hours, minutes] = String(value || "").slice(0, 5).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return hours * 60 + minutes;
}

function timeValue(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function timeLabel(value) {
  const [hours, minutes] = value.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${String(minutes).padStart(2, "0")} ${period}`;
}

function deliveryTimeOptions(startTime = "06:00", endTime = "22:00") {
  const start = timeToMinutes(startTime, 6 * 60);
  const end = timeToMinutes(endTime, 22 * 60);
  if (end < start) return [];
  const options = [];
  for (let minutes = start; minutes <= end; minutes += 30) {
    const value = timeValue(minutes);
    options.push({ value, label: timeLabel(value) });
  }
  return options;
}


function addressLabel(address, result) {
  if (result?.formatted_address) return result.formatted_address;
  const cityLine = [address.city, address.state, address.zip].filter(Boolean).join(" ");
  return cityLine || "Delivery address";
}

export default function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, itemCount, subtotal, updateQuantity, removeItem, reconcileInvalidItems } = useCart();
  const [deliveryStatus, setDeliveryStatus] = useState("unchecked");
  const [deliveryResult, setDeliveryResult] = useState(null);
  const [deliveryError, setDeliveryError] = useState("");
  const [isCheckingDelivery, setIsCheckingDelivery] = useState(false);
  const [address, setAddress] = useState(emptyAddress);
  const [deliverySchedule, setDeliverySchedule] = useState(readSchedule);
  const [orderNote, setOrderNote] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoMessage, setPromoMessage] = useState("");
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [feeSettings, setFeeSettings] = useState({
    serviceFee: 0,
    taxRate: 0,
    deliveryFee: 0,
    freeDeliveryMinutes: null,
    deliveryStartTime: "06:00",
    deliveryEndTime: "22:00",
  });
  const [totalsLoaded, setTotalsLoaded] = useState(false);
  const [cartNotice, setCartNotice] = useState(location.state?.cartNotice || "");
  const [isValidatingCart, setIsValidatingCart] = useState(false);

  const checkoutItems = useMemo(() => cartItemsForCheckout(items), [items]);
  const validationItems = useMemo(() => checkoutItems.map((item, index) => ({
    ...item,
    label: cartItemName(items[index] || {}),
  })), [checkoutItems, items]);
  const cartSignature = useMemo(() => JSON.stringify(validationItems), [validationItems]);

  useEffect(() => {
    const controller = new AbortController();

    Promise.allSettled([
      fetchPublicSettings({ signal: controller.signal }),
      fetchPublicDeliverySettings({ signal: controller.signal }),
    ]).then(([settingsResult, deliveryResult]) => {
      if (controller.signal.aborted) return;

      const settings = settingsResult.status === "fulfilled" ? settingsResult.value : null;
      const delivery = deliveryResult.status === "fulfilled" ? deliveryResult.value : null;

      setFeeSettings({
        serviceFee: money(settings?.payments?.service_fee ?? delivery?.service_fee),
        taxRate: money(settings?.payments?.sales_tax_percentage ?? settings?.payments?.default_tax_rate ?? delivery?.sales_tax_percentage),
        deliveryFee: firstPaidDeliveryFee(delivery),
        freeDeliveryMinutes: delivery?.free_delivery_minutes ?? null,
        minimumNoticeDays: Number(delivery?.minimum_notice_days ?? 14),
        deliveryStartTime: (delivery?.delivery_start_time || "06:00").slice(0, 5),
        deliveryEndTime: (delivery?.delivery_end_time || "22:00").slice(0, 5),
      });
      setTotalsLoaded(Boolean(settings || delivery));
    });

    return () => controller.abort();
  }, []);


  function cartChangedMessage(data) {
    const labels = (data?.invalid_items || [])
      .map((entry) => entry.label)
      .filter(Boolean)
      .slice(0, 2);
    if (labels.length === 1) return `${labels[0]} is no longer available and was removed from your cart.`;
    return data?.message || "One or more items in your cart are no longer available and were removed.";
  }

  function handleCartChanged(data) {
    reconcileInvalidItems(data?.invalid_items || []);
    setAppliedPromo(null);
    setPromoCode("");
    setPromoMessage("");
    setCartNotice(cartChangedMessage(data));
  }


  useEffect(() => {
    if (!items.length) {
      setCartNotice("");
      return undefined;
    }
    const controller = new AbortController();
    validateCheckoutCart({ items: validationItems }, { signal: controller.signal })
      .catch((error) => {
        if (error.name === "AbortError") return;
        if (error.data?.code === "cart_changed") handleCartChanged(error.data);
      });
    return () => controller.abort();
    // Reconcile only when cart item IDs/selections change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSignature]);

  function updateAddress(field, value) {
    setAddress((current) => ({ ...current, [field]: value }));
    setDeliveryStatus("unchecked");
    setDeliveryResult(null);
    setDeliveryError("");
  }


  function updateDeliverySchedule(field, value) {
    setDeliverySchedule((current) => {
      const next = { ...current, [field]: value };
      saveSchedule(next);
      return next;
    });
  }

  async function handleCheckDelivery() {
    setIsCheckingDelivery(true);
    setDeliveryError("");

    try {
      const result = await checkPublicDeliveryAddress(address);
      setDeliveryResult(result);
      setDeliveryStatus(result.status || "unavailable");
    } catch (requestError) {
      setDeliveryResult(null);
      setDeliveryStatus("error");
      setDeliveryError(requestError.message || "Delivery availability could not be checked.");
    } finally {
      setIsCheckingDelivery(false);
    }
  }


  async function handleApplyPromo() {
    if (!promoCode.trim()) return;
    setIsApplyingPromo(true);
    setPromoMessage("");
    try {
      const result = await validateCheckoutCoupon({
        code: promoCode,
        cart_items: cartItemsForCheckout(items),
        delivery_fee: deliveryStatus === "paid" ? String(deliveryResult?.delivery_fee || feeSettings.deliveryFee) : "0.00",
      });
      if (!result.valid) {
        setAppliedPromo(null);
        setPromoMessage(result.message || "That promo code could not be applied.");
        return;
      }
      setAppliedPromo(result);
      setPromoCode(result.code || promoCode.trim().toUpperCase());
      setPromoMessage(result.message || `${result.code} applied.`);
    } catch (error) {
      setAppliedPromo(null);
      setPromoMessage(error.message || "That promo code could not be applied.");
    } finally {
      setIsApplyingPromo(false);
    }
  }

  function handleRemovePromo() {
    setAppliedPromo(null);
    setPromoMessage("");
    setPromoCode("");
  }

  async function handleContinueToCheckout() {
    if (!checkoutReady || isValidatingCart) return;
    setIsValidatingCart(true);
    try {
      await validateCheckoutCart({ items: validationItems });
    } catch (error) {
      if (error.data?.code === "cart_changed") {
        handleCartChanged(error.data);
        return;
      }
      setCartNotice(error.message || "Cart could not be checked. Please try again.");
      return;
    } finally {
      setIsValidatingCart(false);
    }
    const context = {
      deliveryAddress: address,
      deliveryResult,
      deliverySchedule,
      orderNote,
      promoCode: appliedPromo?.code || "",
      appliedPromo,
      items: checkoutItems,
    };
    saveCheckoutContext(context);
    navigate("/checkout", { state: context });
  }

  const minimumNoticeDays = Number(feeSettings.minimumNoticeDays ?? 14);
  const earliestDeliveryDate = new Date();
  earliestDeliveryDate.setHours(0, 0, 0, 0);
  earliestDeliveryDate.setDate(earliestDeliveryDate.getDate() + minimumNoticeDays);
  const minDeliveryDate = toDateInputValue(earliestDeliveryDate);
  const timeOptions = deliveryTimeOptions(feeSettings.deliveryStartTime, feeSettings.deliveryEndTime);
  const validDeliveryTime = timeOptions.some((option) => option.value === deliverySchedule.time);

  const hasItems = items.length > 0;
  const deliveryChecked = deliveryStatus !== "unchecked";
  const deliveryAvailable = ["free", "paid"].includes(deliveryStatus);
  const deliveryDateSelected = Boolean(deliverySchedule.date && deliverySchedule.date >= minDeliveryDate);
  const deliveryTimeSelected = Boolean(deliverySchedule.time && validDeliveryTime);
  const checkoutReady = Boolean(hasItems && deliveryChecked && deliveryAvailable && deliveryDateSelected && deliveryTimeSelected && totalsLoaded && !isValidatingCart);
  const checkoutBlockReason = !hasItems
    ? "Add treats to your cart to continue."
    : !deliveryChecked
    ? "Check your delivery address to continue."
    : !deliveryAvailable
    ? "Delivery must be available for your address before checkout."
    : !deliveryDateSelected || !deliveryTimeSelected
    ? "Select a delivery date and time to continue."
    : !totalsLoaded
    ? "Order totals are still loading. Please wait a moment."
    : "";

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.debug("SugaLumps checkout eligibility", {
      hasItems,
      deliveryChecked,
      deliveryAvailable,
      deliveryDateSelected,
      deliveryTimeSelected,
      totalsLoaded,
      checkoutReady,
    });
  }, [hasItems, deliveryChecked, deliveryAvailable, deliveryDateSelected, deliveryTimeSelected, totalsLoaded, checkoutReady]);

  const deliveryFee = deliveryStatus === "paid" ? money(deliveryResult?.delivery_fee ?? feeSettings.deliveryFee) : 0;
  const serviceFee = items.length ? feeSettings.serviceFee : 0;
  const promoDiscount = appliedPromo?.valid ? money(appliedPromo.merchandise_discount ?? appliedPromo.total_discount) : 0;
  const discountedSubtotal = Math.max(subtotal - promoDiscount, 0);
  const taxRate = feeSettings.taxRate;
  const taxableAmount = discountedSubtotal;
  const tax = taxableAmount * (taxRate / 100);
  const total = discountedSubtotal + deliveryFee + serviceFee + tax;

  return (
    <div className="sl-cart-page">
      <PublicNavbar />

      <main className="sl-cart-main">
        <section className="sl-cart-page-heading">
          <div>
            <h1>Review Your Order</h1>
            <p>Confirm your treats, delivery details, and order total.</p>
          </div>

          <div className="sl-cart-progress">
            <div className="sl-cart-progress-step active">
              <span>
                <ShoppingBag size={18} strokeWidth={1.7} />
              </span>
              <small>Cart</small>
            </div>

            <div className="sl-cart-progress-line" />

            <div className="sl-cart-progress-step">
              <span>
                <MapPin size={18} strokeWidth={1.7} />
              </span>
              <small>Delivery</small>
            </div>

            <div className="sl-cart-progress-line" />

            <div className="sl-cart-progress-step">
              <span>
                <Package size={18} strokeWidth={1.7} />
              </span>
              <small>Review</small>
            </div>
          </div>
        </section>

        {cartNotice && <p className="sl-checkout-helper" role="status">{cartNotice}</p>}

        <div className="sl-cart-layout">
          <div className="sl-cart-content">
            <section className="sl-cart-card sl-delivery-card">
              <div className="sl-card-heading">
                <div className="sl-card-heading-left">
                  <span className="sl-card-number">1</span>

                  <div>
                    <h2>Delivery Address</h2>
                    <p>
                      Enter your address to check delivery availability and cost.
                    </p>
                  </div>
                </div>

                <button type="button" className="sl-text-link">
                  Use saved address
                </button>
              </div>

              <div className="sl-delivery-grid">
                <div className="sl-address-form">
                  <div className="sl-address-row sl-address-row-top">
                    <label>
                      <span>Street Address</span>
                      <input value={address.street} placeholder="123 Main Street" onChange={(event) => updateAddress("street", event.target.value)} />
                    </label>

                    <label>
                      <span>Apt / Suite (optional)</span>
                      <input value={address.apartment} placeholder="Apt 4B" onChange={(event) => updateAddress("apartment", event.target.value)} />
                    </label>
                  </div>

                  <div className="sl-address-row sl-address-row-bottom">
                    <label>
                      <span>City</span>
                      <input value={address.city} placeholder="Silver Spring" onChange={(event) => updateAddress("city", event.target.value)} />
                    </label>

                    <label>
                      <span>State</span>
                      <select value={address.state} onChange={(event) => updateAddress("state", event.target.value)}>
                        <option value="MD">MD</option>
                        <option value="DC">DC</option>
                        <option value="VA">VA</option>
                      </select>
                    </label>

                    <label>
                      <span>ZIP Code</span>
                      <input value={address.zip} placeholder="20910" onChange={(event) => updateAddress("zip", event.target.value)} />
                    </label>
                  </div>

                  <button
                    type="button"
                    className="sl-check-delivery-button"
                    onClick={handleCheckDelivery}
                    disabled={isCheckingDelivery}
                  >
                    <MapPin size={17} strokeWidth={1.8} />
                    {isCheckingDelivery ? "Checking delivery..." : "Check Delivery"}
                  </button>

                  <div className="sl-address-privacy">
                    <LockKeyhole size={13} strokeWidth={1.7} />
                    <span>
                      Your address is used only to check delivery availability.
                    </span>
                  </div>
                </div>

                <div className="sl-delivery-result-side">
                  <div
                    className={`sl-delivery-result ${
                      deliveryStatus === "free"
                        ? "is-free"
                        : deliveryStatus === "paid"
                        ? "is-paid"
                        : deliveryStatus === "unavailable" || deliveryStatus === "error"
                        ? "is-unavailable"
                        : "is-pending"
                    }`}
                  >
                    <span className="sl-delivery-result-icon">
                      {deliveryStatus === "free" ? (
                        <Check size={22} strokeWidth={2} />
                      ) : deliveryStatus === "paid" ? (
                        <Truck size={21} strokeWidth={1.8} />
                      ) : deliveryStatus === "error" ? (
                        <Info size={21} strokeWidth={1.8} />
                      ) : (
                        <MapPin size={21} strokeWidth={1.8} />
                      )}
                    </span>

                    <div>
                      <small>{deliveryStatus === "unchecked" ? "Check delivery availability" : `Delivery to ${addressLabel(address, deliveryResult)}`}</small>

                      <h3>
                        {deliveryStatus === "free"
                          ? "Free delivery"
                          : deliveryStatus === "paid"
                          ? `$${deliveryFee.toFixed(2)} delivery fee`
                          : deliveryStatus === "unavailable"
                          ? "Outside service area"
                          : deliveryStatus === "error"
                          ? "Delivery check unavailable"
                          : "Check delivery availability"}
                      </h3>

                      <p>
                        {deliveryStatus === "free"
                          ? `You're within our ${deliveryResult?.free_delivery_minutes ?? feeSettings.freeDeliveryMinutes ?? "configured"}-minute delivery area.`
                          : deliveryStatus === "paid"
                          ? "This address is within our extended delivery area."
                          : deliveryStatus === "unavailable"
                          ? "We're unable to deliver to this address."
                          : deliveryStatus === "error"
                          ? deliveryError
                          : "Enter your address and check delivery before checkout."}
                      </p>

                      {(deliveryStatus === "free" || deliveryStatus === "paid") && (
                        <span className="sl-result-meta">
                          Approximately {deliveryResult?.duration_minutes} minutes away
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="sl-delivery-helper">
                    Delivery cost is determined after checking your address.
                  </p>

                  <div className="sl-delivery-status-options">
                    <button
                      type="button"
                      className={`sl-delivery-status-option free ${
                        deliveryStatus === "free" ? "active" : ""
                      }`}
                      aria-pressed={deliveryStatus === "free"}
                    >
                      <Check size={14} strokeWidth={1.8} />
                      <span>
                        <strong>Free delivery</strong>
                        <small>Within {deliveryResult?.free_delivery_minutes ?? feeSettings.freeDeliveryMinutes ?? "configured"} minutes</small>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`sl-delivery-status-option paid ${
                        deliveryStatus === "paid" ? "active" : ""
                      }`}
                      aria-pressed={deliveryStatus === "paid"}
                    >
                      <Truck size={14} strokeWidth={1.8} />
                      <span>
                        <strong>Delivery fee applies</strong>
                        <small>${feeSettings.deliveryFee.toFixed(2)} fee</small>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`sl-delivery-status-option unavailable ${
                        deliveryStatus === "unavailable" ? "active" : ""
                      }`}
                      aria-pressed={deliveryStatus === "unavailable"}
                    >
                      <MapPin size={14} strokeWidth={1.8} />
                      <span>
                        <strong>Outside service area</strong>
                        <small>Not available</small>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="sl-cart-card sl-delivery-schedule-card">
              <div className="sl-card-heading">
                <div className="sl-card-heading-left">
                  <span className="sl-card-number">2</span>
                  <div>
                    <h2>Delivery Date &amp; Time</h2>
                    <p>Choose when you'd like your sweets delivered.</p>
                  </div>
                </div>
              </div>

              <div className="sl-schedule-grid">
                <label>
                  <span>Delivery Date</span>
                  <input type="date" value={deliverySchedule.date} min={minDeliveryDate} onChange={(event) => updateDeliverySchedule("date", event.target.value)} />
                </label>

                <label>
                  <span>Delivery Time</span>
                  <select value={deliverySchedule.time} onChange={(event) => updateDeliverySchedule("time", event.target.value)}>
                    <option value="">Select a delivery time</option>
                    {timeOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <p className="sl-schedule-helper">Orders require at least {minimumNoticeDays} days' notice.</p>
            </section>

            <section className="sl-cart-card sl-treats-card">
              <div className="sl-card-heading sl-treats-heading">
                <div className="sl-card-heading-left">
                  <span className="sl-card-number">3</span>
                  <h2>Your Treats</h2>
                </div>

                <div className="sl-cart-column-headings">
                  <span>Price</span>
                  <span>Quantity</span>
                  <span>Total</span>
                </div>
              </div>

              <div className="sl-cart-items">
                {items.length ? (
                  items.map((item) => (
                    <article className="sl-cart-item" key={item.id}>
                      {cartItemImage(item) ? (
                        <img
                          src={cartItemImage(item)}
                          alt={cartItemName(item)}
                          className="sl-cart-item-image"
                        />
                      ) : (
                        <div className="sl-cart-item-image sl-cart-image-placeholder" aria-hidden="true">
                          <ShoppingBag size={24} strokeWidth={1.4} />
                        </div>
                      )}

                      <div className="sl-cart-item-info">
                        <div className="sl-cart-item-title-row">
                          <h3>{cartItemName(item)}</h3>

                          {cartItemBadge(item) && (
                            <span className="sl-item-pill">{cartItemBadge(item)}</span>
                          )}

                          {cartItemSecondBadge(item) && (
                            <span className="sl-item-pill">
                              {cartItemSecondBadge(item)}
                            </span>
                          )}
                        </div>

                        <p>{cartItemDetails(item)}</p>

                        <div className="sl-cart-item-actions">
                          <button type="button">{cartItemEditLabel(item)}</button>
                          <span />
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <strong className="sl-cart-item-price">
                        ${money(item.unit_price).toFixed(2)}
                      </strong>

                      <div className="sl-quantity-control">
                        <button
                          type="button"
                          disabled={item.quantity <= 1}
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          <Minus size={13} strokeWidth={1.8} />
                        </button>

                        <span>{item.quantity}</span>

                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          <Plus size={13} strokeWidth={1.8} />
                        </button>
                      </div>

                      <strong className="sl-cart-item-total">
                        ${(money(item.unit_price) * item.quantity).toFixed(2)}
                      </strong>
                    </article>
                  ))
                ) : (
                  <div className="sl-cart-empty-state">
                    <ShoppingBag size={34} strokeWidth={1.4} />
                    <h3>Your cart is empty</h3>
                    <p>Add treats from the menu and they will appear here.</p>
                    <Link to="/menu" className="sl-back-menu-button">
                      <ArrowLeft size={15} strokeWidth={1.8} />
                      Back to Menu
                    </Link>
                  </div>
                )}
              </div>

              <div className="sl-order-note">
                <label htmlFor="order-note">
                  <Package size={15} strokeWidth={1.7} />
                  Order Notes <span>(optional)</span>
                </label>

                <div className="sl-order-note-box">
                  <textarea
                    id="order-note"
                    value={orderNote}
                    maxLength={250}
                    onChange={(event) => setOrderNote(event.target.value)}
                    placeholder="Add any special instructions, allergies, or notes for your order..."
                  />

                  <small>{orderNote.length} / 250</small>
                </div>
              </div>
            </section>
          </div>

          <aside className="sl-order-summary-column">
            <section className="sl-summary-card">
              <div className="sl-summary-title">
                <ShoppingBag size={19} strokeWidth={1.7} />
                <h2>Order Summary</h2>
              </div>

              <div className="sl-summary-lines">
                <div className="sl-summary-line">
                  <span>Subtotal ({itemCount} items)</span>
                  <strong>${subtotal.toFixed(2)}</strong>
                </div>

                {appliedPromo?.valid && (
                  <div className="sl-summary-line sl-summary-promo-line">
                    <span>
                      Promo ({appliedPromo.code})
                      <button type="button" onClick={handleRemovePromo}>Remove</button>
                    </span>
                    <strong>-${promoDiscount.toFixed(2)}</strong>
                  </div>
                )}

                <div className="sl-summary-line sl-summary-delivery-line">
                  <span>
                    Delivery
                    <small>Based on delivery address</small>
                  </span>

                  <strong
                    className={
                      deliveryStatus === "free"
                        ? "sl-summary-free"
                        : deliveryStatus === "unavailable"
                        ? "sl-summary-unavailable"
                        : ""
                    }
                  >
                    {deliveryStatus === "free"
                      ? "Free"
                      : deliveryStatus === "paid"
                      ? `$${deliveryFee.toFixed(2)}`
                      : deliveryStatus === "unavailable" || deliveryStatus === "error"
                      ? "Unavailable"
                      : "After check"}
                  </strong>
                </div>

                <div className="sl-summary-line">
                  <span className="sl-inline-label">
                    Service Fee
                    <Info size={13} strokeWidth={1.7} />
                  </span>
                  <strong>${serviceFee.toFixed(2)}</strong>
                </div>

                <div className="sl-summary-line">
                  <span>Sales Tax ({taxRate.toFixed(1)}%)</span>
                  <strong>${tax.toFixed(2)}</strong>
                </div>
              </div>

              <div className="sl-summary-total">
                <span>Total</span>
                <strong>${total.toFixed(2)}</strong>
              </div>

              {deliveryStatus === "free" && (
                <div className="sl-free-delivery-banner">
                  <Gift size={16} strokeWidth={1.7} />
                  Your address qualifies for FREE delivery!
                </div>
              )}

              <div className="sl-promo-row">
                <span>
                  <Tag size={16} strokeWidth={1.7} />
                </span>

                <input
                  value={promoCode}
                  onChange={(event) => setPromoCode(event.target.value)}
                  placeholder="Enter promo code"
                  disabled={isApplyingPromo || appliedPromo?.valid}
                />

                <button type="button" onClick={handleApplyPromo} disabled={isApplyingPromo || !promoCode.trim() || appliedPromo?.valid}>
                  {isApplyingPromo ? "Applying" : appliedPromo?.valid ? "Applied" : "Apply"}
                </button>
              </div>

              {promoMessage && (
                <p className={`sl-promo-message ${appliedPromo?.valid ? "is-valid sr-only" : "is-invalid"}`}>
                  {promoMessage}
                </p>
              )}

              <button type="button" className="sl-gift-note-button">
                <span>
                  <Gift size={16} strokeWidth={1.7} />
                  Add a gift note (optional)
                </span>

                <span>⌄</span>
              </button>

              <div className="sl-secure-note">
                <LockKeyhole size={13} strokeWidth={1.7} />
                Secure checkout. Your information is protected.
              </div>

              {checkoutBlockReason && (
                <p className="sl-checkout-helper" role="status">
                  {checkoutBlockReason}
                </p>
              )}

              <button
                type="button"
                className="sl-checkout-button"
                disabled={!checkoutReady}
                onClick={handleContinueToCheckout}
              >
                <LockKeyhole size={15} strokeWidth={1.8} />
                {isValidatingCart ? "Checking Cart..." : "Continue to Checkout"}
                <ArrowRight size={16} strokeWidth={1.8} />
              </button>

              <Link to="/menu" className="sl-back-menu-button">
                <ArrowLeft size={15} strokeWidth={1.8} />
                Back to Menu
              </Link>
            </section>

            <section className="sl-delivery-details-card">
              <div className="sl-delivery-details-title">
                <Truck size={20} strokeWidth={1.7} />
                <h2>Delivery Details</h2>
              </div>

              <div className="sl-detail-row">
                <Truck size={17} strokeWidth={1.6} />
                <div>
                  <strong>Delivery only</strong>
                  <small>No pickup available</small>
                </div>
              </div>

              <div className="sl-detail-row">
                <Clock3 size={17} strokeWidth={1.6} />
                <div>
                  <strong>Estimated time</strong>
                  <small>60–90 min</small>
                </div>
              </div>

              <div className="sl-detail-row">
                <MapPin size={17} strokeWidth={1.6} />
                <div>
                  <strong>Service area</strong>
                  <small>Silver Spring &amp; DMV</small>
                  <button type="button">View areas</button>
                </div>
              </div>

              <p className="sl-delivery-details-note">
                Prices may vary by selection. Final details confirmed at
                checkout.
              </p>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
}